import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import MovieCard from "@/components/feature/MovieCard";
import { movies } from "@/mocks/movies";
import { filterCountries, filterGenres, filterYears, sortOptions } from "@/mocks/categories";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const PAGE_SIZE = 6;

export default function Category() {
  const { type = "电影" } = useParams();
  const [country, setCountry] = useState("全部");
  const [year, setYear] = useState("全部");
  const [genre, setGenre] = useState("全部");
  const [sort, setSort] = useState("rating");
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  useDocumentMeta({
    title: `${type}分类 - 八戒影视`,
    description: `八戒影视${type}分类页，提供丰富的${type}资源与热门作品推荐，支持按国家、年份、类型筛选并按热度或上映时间排序。`,
    keywords: `八戒影视,${type},${type}推荐,热门${type},影视资源`,
  });

  const filtered = useMemo(() => {
    let list = movies.filter((m) => m.type === type);
    if (country !== "全部") list = list.filter((m) => m.country === country);
    if (year !== "全部") list = list.filter((m) => String(m.year) === year);
    if (genre !== "全部") list = list.filter((m) => m.genres.includes(genre));
    const sorted = [...list];
    if (sort === "rating") sorted.sort((a, b) => b.rating - a.rating);
    else if (sort === "year") sorted.sort((a, b) => b.year - a.year);
    else sorted.sort((a, b) => a.title.localeCompare(b.title, "zh-Hans-CN"));
    return sorted;
  }, [type, country, year, genre, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const hasFilter = country !== "全部" || year !== "全部" || genre !== "全部";
  const knownType = ["电影", "电视剧", "综艺"].includes(type);

  const resetFilters = () => {
    setCountry("全部");
    setYear("全部");
    setGenre("全部");
    setSort("rating");
    setPage(1);
  };

  const update = (setter: (v: string) => void) => (value: string) => {
    setter(value);
    setPage(1);
  };

  const selectClass =
    "w-full md:w-auto px-3 py-2 pr-8 text-sm rounded-md bg-background-50 border border-background-300 text-foreground-700 outline-none focus:border-primary-400 cursor-pointer";

  const FilterFields = (
    <>
      <label className="flex flex-col gap-1.5 md:flex-row md:items-center">
        <span className="text-xs text-foreground-500 md:w-12">国家</span>
        <select value={country} onChange={(e) => update(setCountry)(e.target.value)} className={selectClass}>
          {filterCountries.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </label>
      <label className="flex flex-col gap-1.5 md:flex-row md:items-center">
        <span className="text-xs text-foreground-500 md:w-12">年份</span>
        <select value={year} onChange={(e) => update(setYear)(e.target.value)} className={selectClass}>
          {filterYears.map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
      </label>
      <label className="flex flex-col gap-1.5 md:flex-row md:items-center">
        <span className="text-xs text-foreground-500 md:w-12">类型</span>
        <select value={genre} onChange={(e) => update(setGenre)(e.target.value)} className={selectClass}>
          {filterGenres.map((g) => (
            <option key={g} value={g}>{g}</option>
          ))}
        </select>
      </label>
      <label className="flex flex-col gap-1.5 md:flex-row md:items-center">
        <span className="text-xs text-foreground-500 md:w-12">排序</span>
        <select value={sort} onChange={(e) => update(setSort)(e.target.value)} className={selectClass}>
          {sortOptions.map((s) => (
            <option key={s.key} value={s.key}>{s.label}</option>
          ))}
        </select>
      </label>
      {hasFilter && (
        <button
          type="button"
          onClick={resetFilters}
          className="md:ml-auto px-4 py-2 text-sm rounded-md border border-background-300 text-foreground-600 hover:border-accent-400 hover:text-accent-600 transition-colors whitespace-nowrap cursor-pointer"
        >
          重置筛选
        </button>
      )}
    </>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: `${type}分类` }]} />

      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">{type}分类</h1>
        <p className="text-sm text-foreground-500 mt-2 max-w-2xl">
          这里汇集了八戒影视平台内的{type}资源。你可以按国家、年份与影片类型进行筛选，并按热门程度或上映时间排序，
          快速找到值得一看的作品。
        </p>
      </div>

      {/* 筛选栏 */}
      <div className="rounded-lg border border-background-200 bg-background-100 p-4 mb-6">
        <div className="hidden md:flex md:flex-wrap md:items-center gap-4">{FilterFields}</div>
        <div className="md:hidden flex items-center justify-between">
          <span className="text-sm text-foreground-600">
            共 <strong className="text-primary-600">{filtered.length}</strong> 部作品
          </span>
          <button
            type="button"
            onClick={() => setShowFilters((v) => !v)}
            className="flex items-center gap-1.5 px-4 py-2 text-sm rounded-md border border-background-300 text-foreground-700 cursor-pointer"
          >
            <i className="ri-equalizer-line"></i>
            筛选
          </button>
        </div>
        {showFilters && <div className="md:hidden flex flex-col gap-4 mt-4 pt-4 border-t border-background-200">{FilterFields}</div>}
      </div>

      {pageItems.length > 0 ? (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {pageItems.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
          <Pagination current={currentPage} total={totalPages} onChange={setPage} />
        </>
      ) : (
        <EmptyState
          title={knownType ? "暂时没有符合条件的作品" : "该分类内容正在筹备中"}
          description={
            knownType
              ? "换个筛选条件试试，或者重置筛选查看全部作品。"
              : "该分类暂未上线内容，你可以先浏览电影、电视剧或综艺分类。"
          }
          actionLabel={knownType ? "重置筛选条件" : "去看看电影分类"}
          actionTo={knownType ? undefined : "/category/电影"}
          onAction={knownType ? resetFilters : undefined}
        />
      )}

      <div className="mt-10 flex flex-wrap gap-3">
        {["电影", "电视剧", "综艺"].filter((t) => t !== type).map((t) => (
          <Link
            key={t}
            to={`/category/${t}`}
            className="px-4 py-2 text-sm rounded-md bg-background-100 border border-background-200 text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer"
          >
            浏览{t}分类
          </Link>
        ))}
      </div>
    </div>
  );
}